import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { comics, genres } from "@/mocks/comics";
import ComicCard from "@/components/feature/ComicCard";

export default function Ranking() {
  const [params, setParams] = useSearchParams();
  const activeFilter = params.get("filter") === "completed" ? "completed" : "all";
  const activeGenre = params.get("genre") ?? "";
  const keyword = params.get("q") ?? "";
  const [localQ, setLocalQ] = useState(keyword);

  const filtered = useMemo(() => {
    let list = [...comics];
    if (activeFilter === "completed") {
      list = list.filter((c) => c.status === "完结");
    }
    if (activeGenre) {
      list = list.filter((c) => c.genres.includes(activeGenre));
    }
    if (keyword.trim()) {
      const k = keyword.trim().toLowerCase();
      list = list.filter(
        (c) =>
          c.title.toLowerCase().includes(k) ||
          c.author.toLowerCase().includes(k) ||
          c.genres.some((g) => g.includes(keyword.trim())) ||
          c.tags.some((t) => t.includes(keyword.trim()))
      );
    }
    if (activeFilter === "completed") {
      list.sort((a, b) => b.rating - a.rating);
    } else {
      list.sort((a, b) => b.rating - a.rating);
    }
    return list;
  }, [activeFilter, activeGenre, keyword]);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setParam("q", localQ.trim());
  };

  return (
    <>
      {/* Page hero */}
      <section className="border-b border-background-200/70 bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <nav aria-label="面包屑" className="mb-4 flex items-center gap-1 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-500">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">漫画榜单</span>
          </nav>
          <h1 className="font-display text-3xl text-foreground-950 md:text-4xl">韩国漫画排行榜</h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-foreground-600">
            免费韩国漫画榜单与完结推荐聚合页。按题材精准筛选，结合搜索快速定位你想看的韩漫，热门冷门全覆盖。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
        {/* Filter bar */}
        <div className="mb-7 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          {/* Tabs */}
          <div className="inline-flex w-fit rounded-full bg-background-100 p-1" role="tablist" aria-label="榜单切换">
            <button
              role="tab"
              aria-selected={activeFilter === "all"}
              onClick={() => setParam("filter", "")}
              className={`whitespace-nowrap rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                activeFilter === "all"
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              综合热榜
            </button>
            <button
              role="tab"
              aria-selected={activeFilter === "completed"}
              onClick={() => setParam("filter", "completed")}
              className={`whitespace-nowrap rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                activeFilter === "completed"
                  ? "bg-accent-500 text-background-50"
                  : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              完结推荐
            </button>
          </div>

          {/* Search */}
          <form onSubmit={submitSearch} className="flex items-center overflow-hidden rounded-full border border-background-300 bg-background-50 focus-within:border-primary-400">
            <input
              type="text"
              value={localQ}
              onChange={(e) => setLocalQ(e.target.value)}
              placeholder="搜索韩漫名称 / 作者 / 题材"
              aria-label="搜索韩漫"
              className="h-10 w-full bg-transparent px-4 text-sm text-foreground-800 outline-none placeholder:text-foreground-400 lg:w-64"
            />
            <button type="submit" aria-label="搜索" className="flex h-10 w-12 shrink-0 items-center justify-center bg-primary-500 text-background-50 transition-colors hover:bg-primary-600">
              <i className="ri-search-line text-sm"></i>
            </button>
          </form>
        </div>

        {/* Genre chips */}
        <div className="mb-7 flex flex-wrap items-center gap-2">
          <span className="mr-1 whitespace-nowrap text-xs text-foreground-500">题材筛选：</span>
          <button
            onClick={() => setParam("genre", "")}
            className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
              !activeGenre ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:text-primary-500"
            }`}
          >
            全部
          </button>
          {genres.map((g) => (
            <button
              key={g.name}
              onClick={() => setParam("genre", activeGenre === g.name ? "" : g.name)}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                activeGenre === g.name ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:text-primary-500"
              }`}
            >
              {g.name}
            </button>
          ))}
        </div>

        {/* Result count */}
        <p className="mb-5 text-xs text-foreground-500">
          共找到 <span className="font-bold text-primary-500">{filtered.length}</span> 部韩漫
          {activeFilter === "completed" && " · 已完结"}
          {activeGenre && ` · 题材：${activeGenre}`}
          {keyword && ` · 关键词：${keyword}`}
        </p>

        {/* Results */}
        {filtered.length > 0 ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {filtered.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <i className="ri-file-search-line text-4xl text-foreground-300"></i>
            <p className="mt-4 text-sm font-medium text-foreground-700">没有找到匹配的韩漫</p>
            <p className="mt-1 text-xs text-foreground-500">换个题材或关键词再试试吧</p>
            <button
              onClick={() => {
                setParams(new URLSearchParams(), { replace: true });
                setLocalQ("");
              }}
              className="mt-5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              重置筛选
            </button>
          </div>
        )}
      </section>
    </>
  );
}